import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { StripeAddressElementOptions } from '@stripe/stripe-js';
import {
  ItemOp,
  PagingData,
  ShopingCart,
  ShoppingCartPayload,
} from '../models';
import { Address } from '../models/address.model';
import {
  AmmFavoriteBackend,
  AmmPrice,
  AmmShoppingCartBackEnd,
  Product,
} from '../models/catalog.model';
import { Favorite } from '../models/favorite.model';
import { ProductService, ShoppingCartService } from '../services';
import { ShippingOption } from './../models/catalog.model';
import { GroupItemByStore } from './../models/shoppingCart.interface';
import { CommonStore } from './common.store';
import { HomeProductStore } from './home-product.store';
import { error } from 'node:console';
import { catchError, of, throwError } from 'rxjs';

type ShoppingInfo = {
  favorites: Favorite[];
  // Stores all the shopping cart items
  // either from memory or db
  shoppingCarts: ShopingCart[];

  // A backup store which used to restore
  // the state of the shopping cart; It needs
  // to be sync with shopping cart whenever the shopping
  // carts above is udpated.
  undoShoppingCarts: ShopingCart[];
  productsGrouppedByStore: GroupItemByStore[];
  recommandForYou: Product[];
  selectedShippingOption: Map<string, ShippingOption>;
  orderSubtotal: AmmPrice | undefined;
  orderTotal: AmmPrice | undefined;
  shippingFee: AmmPrice | undefined;
  shippingAddressesOption: StripeAddressElementOptions;
  ShopingCartBackend: AmmShoppingCartBackEnd | undefined;
  shortInventoryMerchCodes: string[]
};

const initialState: ShoppingInfo = {
  favorites: [],
  shoppingCarts: [],
  undoShoppingCarts: [],
  productsGrouppedByStore: [],
  recommandForYou: [],
  selectedShippingOption: new Map<string, ShippingOption>(),
  orderSubtotal: undefined,
  orderTotal: undefined,
  shippingFee: undefined,
  shippingAddressesOption: <StripeAddressElementOptions>{
    mode: 'shipping',
  },
  ShopingCartBackend: undefined,
  shortInventoryMerchCodes: []
};
export const ShoppingInfoStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(initialState),
  withComputed((shoppingInfo) => ({
    shoppingCartCount: computed(() => {
      // TODO: it's possible that here we need to use the available items, the reason we don't do it right now
      // TODO: because it should show the all the items counts
      const carts = shoppingInfo.shoppingCarts();
      if (carts.length > 0) {
        return carts
          .filter((x) => (x.product?.code?.length || 0) > 0)
          .map((x) => x.qty)
          .reduce((prev, cur) => {
            return prev + cur;
          }, 0);
      }
      return 0;
    }),
    favoriteProducts: computed(() => {
      const result: Favorite[] = [];

      const favorites = shoppingInfo.favorites();
      const addFavorites = favorites.filter((x) => x.action === 'ADD');
      const removeFavorites = favorites.filter((x) => x.action === 'REMOVE');

      addFavorites.forEach((x) => {
        const isInRemoved =
          removeFavorites.findIndex((rf) => rf.merchCode === x.merchCode) != -1;
        if (!isInRemoved) {
          result.push(x);
        }
      });

      return result;
    }),
    favoriteCount: computed(() => {
      const favorites = shoppingInfo.favorites();
      const addFavorites = favorites.filter((x) => x.action === 'ADD');
      const removeFavorites = favorites.filter((x) => x.action === 'REMOVE');
      let counter = 0;

      addFavorites.forEach((af) => {
        const isRemoved =
          removeFavorites.findIndex((rf) => rf.merchCode === af.merchCode) ==
          -1;
        if (!isRemoved) {
          counter++;
        }
      });

      return counter;
    }),
    totalOrderAmount: computed(() => {
      const items = shoppingInfo
        .shoppingCarts()
        .filter(
          (x) =>
            x.qty > 0 &&
            (x.product?.inventory ?? 0) >= x.qty &&
            x.product?.status !== 'INACTIVE' &&
            x.product?.status !== 'ARCHIVED' &&
            x.product?.status !== 'PAUSED'
        );

      const itemPrices = items?.map(
        (x) => (x.qty || 0) * (x.product?.curPrice?.amount || 0)
      );

      return itemPrices?.reduce((acc, cur) => acc + cur, 0);
    }),
    totalOrderCount: computed(() => {
      const itemCounts = shoppingInfo
        .shoppingCarts()
        .filter(
          (x) =>
            x.qty > 0 &&
            (x.product?.inventory ?? 0) >= x.qty &&
            x.product?.status !== 'INACTIVE' &&
            x.product?.status !== 'ARCHIVED' && 
            x.product?.status !== 'PAUSED'
        )
        .map((x) => x.qty);

      return itemCounts?.reduce((acc, cur) => (acc || 0) + (cur || 0), 0);
    }),
    availableShoppingCarts: computed(() => {
      return shoppingInfo
        .shoppingCarts()
        .filter(
          (x) =>
            x.qty > 0 &&
            (x.product?.inventory ?? 0) >= x.qty &&
            x.product?.status !== 'INACTIVE' &&
            x.product?.status !== 'ARCHIVED'  &&
            x.product?.status !== 'PAUSED'
        );
    }),
    unavailableShoppingCarts: computed(() => {
      return shoppingInfo
        .shoppingCarts()
        .filter(
          (x) =>
          
            x.product?.status === 'INACTIVE' ||
          x.product?.status === 'ARCHIVED' ||
          x.product?.status === 'PAUSED' ||
          (x.product?.inventory ?? 0) <= 0 || // product has been sold more than the inventory
          (x.product?.inventory ?? 0) < x.qty // product current inventory cannot fulfil the user order quantity 
        );
    }),
    shoppingCartStoreCodes: computed(() => {
      const availableShoppingCarts = shoppingInfo
        .shoppingCarts()
        .filter(
          (x) =>
            x.qty > 0 &&
            (x.product?.inventory ?? 0) >= x.qty &&
            x.product?.status !== 'INACTIVE' &&
            x.product?.status !== 'ARCHIVED' &&
            x.product?.status !== 'PAUSED'
        );

      return availableShoppingCarts.map((x) => x.product?.code);
    }),
    orderSubTotalWithShipping: computed(() => {
      const subTotal = shoppingInfo.orderSubtotal()?.amount || 0;
      const shippingFee = shoppingInfo.shippingFee()?.amount || 0;
      return subTotal + shippingFee;
    }),
    orderTax: computed(() => {
      const orderTotal = shoppingInfo.orderTotal()?.amount || 0;
      const subTotal = shoppingInfo.orderSubtotal()?.amount || 0;
      const shippingFee = shoppingInfo.shippingFee()?.amount || 0;
      return Math.max(orderTotal - subTotal - shippingFee, 0);
    }),
    hasOutOfStock: computed(() => {
      const groupResult = shoppingInfo.productsGrouppedByStore();
      let result = false;

      const flatMap = groupResult.flatMap((x) => x.carts);
      // if the current product inventory is negative, or the current inventory is 
      // less than the order quantity, we consider this scenario as out of stock 
      const index = flatMap.findIndex(
        (x) => (x.product?.inventory || 0) <=0 || (x.product?.inventory || 0) < x.qty
      );
      result = index != -1;
      return result;
    }),
    hasItemInUndoCarts: computed(() => {
      return shoppingInfo.undoShoppingCarts().length > 0;
    })
  })),
  withMethods(
    (
      store,
      shoppingService = inject(ShoppingCartService),
      productSrv = inject(ProductService),
      commonStore = inject(CommonStore),
      homeProductStore = inject(HomeProductStore)
    ) => ({
      loadAllFavorites() {
        shoppingService
          .getFavorites()
          .subscribe((response: PagingData<AmmFavoriteBackend>) => {
            if (response && response.dataList) {
              const favorites = response.dataList;
              const favoritesToLoad = favorites.map((x) =>
                Favorite.formatData(x)
              );
              patchState(store, {
                favorites: favoritesToLoad,
              });
            }
          });
      },
      clearFavorites() {
        patchState(store, {
          favorites: []
        })
      },
      increaseFavorite(merchCode: string) {
        const existFavorite = store
          .favorites()
          .findIndex((x) => x.merchCode === merchCode && x.action === 'ADD');
        if (existFavorite == -1) {
          patchState(store, {
            favorites: [
              ...store.favorites(),
              { merchCode: merchCode, action: 'ADD' },
            ],
          });
        }
        const itemToSave = store
          .favorites()
          .find((x) => x.merchCode === merchCode && x.action == 'ADD');
        if (itemToSave) {
          this.saveSingleFavoriteToDB(itemToSave);
        }
      },
      decreaseFavorite(merchCode: string) {
        const existFavorite = store
          .favorites()
          .findIndex((x) => x.merchCode === merchCode);
        if (existFavorite == -1) {
          patchState(store, {
            favorites: [
              ...store.favorites(),
              { merchCode: merchCode, action: 'REMOVE' },
            ],
          });
        } else {
          const existAddFavorite = store
            .favorites()
            .findIndex((x) => x.merchCode === merchCode && x.action === 'ADD');
          if (existAddFavorite != -1) {
            patchState(store, {
              favorites: store
                .favorites()
                .map((x) =>
                  x.merchCode == merchCode ? { ...x, action: 'REMOVE' } : x
                ),
            });
          }
        }

        const itemToSave = store
          .favorites()
          .find((x) => x.merchCode === merchCode && x.action == 'REMOVE');
        if (itemToSave) {
          this.saveSingleFavoriteToDB(itemToSave);
        }
      },
      saveFavoriteToDB() {
        const curFavorites = store.favorites();
        const favoritesToDb = <ShoppingCartPayload>{};
        favoritesToDb.itemOps = curFavorites.map(
          (x) =>
            <ItemOp>{
              merchCode: x.merchCode,
              action: x.action,
            }
        );
        // In order to send http request, we have to subscribe
        shoppingService.updateFavorites(favoritesToDb).subscribe(() => {
          this.loadAllFavorites();
        });
      },
      saveSingleFavoriteToDB(favorite: Favorite) {
        const favoritesToDb = <ShoppingCartPayload>{};
        favoritesToDb.itemOps = [
          <ItemOp>{
            merchCode: favorite.merchCode,
            action: favorite.action,
          },
        ];

        // In order to send http request, we have to subscribe
        shoppingService.updateFavorites(favoritesToDb).subscribe(() => {
          this.loadAllFavorites();
        });
      },
      increaseShopingCart(product: Product | Favorite) {
        const isSingleProduct =  product.isMainVariant && (product.variantsCount??0) == 1 && (product.variantMerchs?.length??0) === 1;
        const productCode = isSingleProduct && product.variantMerchs ? product.variantMerchs[0].code : product.code;
        const index = store
          .shoppingCarts()
          .findIndex((x) => x.product?.code === productCode);
        if (index == -1) {
          patchState(store, {
            shoppingCarts: [
              ...store.shoppingCarts(),
              { qty: 1, product: product },
            ],
          });
        } else {
          patchState(store, (state) => ({
            shoppingCarts: state.shoppingCarts.map((x, idx) =>
              x.product?.code === product.code
                ? { ...x, qty: Math.min(x.qty + 1, product.inventory || x.qty) }
                : x
            ),
          }));
        }
        const itemToSave = store
          .shoppingCarts()
          .find((s) => s.product?.code === product.code);
        this.saveSingleShoppingCartToDB(
          itemToSave?.product?.code || product.code || '',
          itemToSave?.qty || 0
        );
      },
      decreaseShopingCart(product: Product | Favorite) {
        const isSingleProduct =  product.isMainVariant && (product.variantsCount??0) == 1 && (product.variantMerchs?.length??0) === 1;
        const productCode = isSingleProduct && product.variantMerchs ? product.variantMerchs[0].code : product.code;
        const index = store
          .shoppingCarts()
          .findIndex((x) => x.product?.code === productCode);
        if (index != -1) {
          patchState(store, (state) => ({
            shoppingCarts: state.shoppingCarts.map((x, idx) =>
              idx == index ? { ...x, qty: Math.max(x.qty - 1, 0) } : x
            ),
          }));
          const itemToSave = store
            .shoppingCarts()
            .find((s) => s.product?.code === product.code);
          this.saveSingleShoppingCartToDB(
            itemToSave?.product?.code || product.code || '',
            itemToSave?.qty || 0
          );
        }
      },
      setShoppingCart(qty: number, product: Product) {
        const index = store
          .shoppingCarts()
          .findIndex((x) => x.product?.code === product.code);
        if (index == -1) {
          patchState(store, {
            shoppingCarts: [
              ...store.shoppingCarts(),
              { qty: qty, product: product },
            ],
          });
        } else {
          const findProduct = store.shoppingCarts().find(x => x.product?.code === product.code);
          patchState(store, {
            shoppingCarts: store
              .shoppingCarts()
              .map((sc, idx) =>
                sc.product?.code === product.code ? { ...sc, qty: (findProduct?.qty || 0) + qty } : sc
              ),
          });
        }
        const itemToSave = store
          .shoppingCarts()
          .find((s) => s.product?.code === product.code);
        this.saveSingleShoppingCartToDB(
          itemToSave?.product?.code || product.code || '',
          itemToSave?.qty || 0
        );
      },
      setVariantShoppingCart(qty: number, product: Product) {
        const index = store
          .shoppingCarts()
          .findIndex((x) => x.product?.code === product.code);
        if (index == -1) {
          patchState(store, {
            shoppingCarts: [
              ...store.shoppingCarts(),
              { qty: qty, product: product },
            ],
          });
        } else {
          patchState(store, {
            shoppingCarts: store
              .shoppingCarts()
              .map((sc, idx) =>
                sc.product?.code === product.code ? { ...sc, qty: qty } : sc
              ),
          });
        }
        const itemToSave = store
          .shoppingCarts()
          .find((s) => s.product?.code === product.code);
        this.saveSingleShoppingCartToDB(
          itemToSave?.product?.code || product.code || '',
          itemToSave?.qty || 0
        );
      },
      saveShoppingCartsToDB() {
        const carts = store.shoppingCarts();
        const cartsToDb = <ShoppingCartPayload>{};

        cartsToDb.itemOps = carts.map(
          (x) =>
            <ItemOp>{
              merchCode: x.product?.code,
              qty: x.qty,
            }
        );

        shoppingService.updateShoppingCart(cartsToDb).subscribe(() => {
          //this.loadShoppingCarts();
        });
      },
      saveSingleShoppingCartToDB(merchCode: string, qty: number) {
        const cartsToDb = <ShoppingCartPayload>{};

        cartsToDb.itemOps = [
          <ItemOp>{
            merchCode: merchCode,
            qty: qty,
          },
        ];

        shoppingService.updateShoppingCart(cartsToDb).subscribe((response) => {
          console.log(response);
          if (response) {

            patchState(store, {
              shoppingCarts: store.shoppingCarts().filter((x) => x.qty > 0),
            });
            this.clearUndoShoppingCarts();
          } else {
            this.clearShoppingCarts();
            this.clearUndoShoppingCarts();
          }
        });
      },
      loadShoppingCarts(
        shouldGroupByStore: boolean = false,
        shouldInitShippingOption: boolean = false
      ) {
        this.clearShippingOption();
        this.clearGroupProductByStore();
        shoppingService
          .getShoppingCart()
          .subscribe((response: PagingData<AmmShoppingCartBackEnd>) => {
            if (response && Array.isArray(response) && response[0]['error']) {
              if (
                response[0]['error'] &&
                (response[0]['code'] as string).includes('403')
              ) {
                patchState(store, {
                  recommandForYou: homeProductStore.products()
                })              }
              return;
            }
            if (response && response.dataList && response.dataList.length > 0) {
              const itemsInDb = response?.dataList[0]?.items?.map(
                (x) =>
                  <ShopingCart>{
                    product: x.merchandise ? x.merchandise : <Product>{code: x.merchCode} ,
                    qty: x?.qty,
                    shippingPolicy: x.shippingPolicy,
                  }
              );
              const orderTotal = response?.dataList[0]?.orderTotal;
              const subTotal = response?.dataList[0]?.itemSubTotal;
              const shippingFee = response?.dataList[0].shippingFee;
              if (itemsInDb) {
                patchState(store, {
                  shoppingCarts: itemsInDb,
                  orderSubtotal: subTotal,
                  orderTotal: orderTotal,
                  shippingFee: shippingFee,
                  ShopingCartBackend: response.dataList[0],
                });
              }
            }
            this.loadRecommondForYou();
            if (shouldGroupByStore) {
              const currentShoppingCarts = store.shoppingCarts();
              this.groupProductsByStore(currentShoppingCarts);
            }
            if (shouldInitShippingOption) {
              this.initShippingOption();
            }
          });
      },
      updateOrderFees(
        subTotal: AmmPrice | undefined,
        orderTotal: AmmPrice | undefined,
        shippingFee: AmmPrice | undefined
      ) {
        patchState(store, {
          orderSubtotal: subTotal,
          orderTotal: orderTotal,
          shippingFee: shippingFee,
        });
      },
      clearOrderFees() {
        patchState(store, {
          orderSubtotal: undefined,
          orderTotal: undefined,
          shippingFee: undefined,
        });
      },
      groupProductsByStore(carts: ShopingCart[]) {
        const currentShoppingCarts = carts;
        const result: GroupItemByStore[] = [];

        currentShoppingCarts.forEach((cart) => {
          if (cart && cart.product && cart.product.store) {
            const isItemFound =
              result.findIndex(
                (r) => r.store.code === cart?.product?.store?.code
              ) !== -1;
            if (!isItemFound) {
              result.push({
                store: cart?.product?.store,
                carts: [cart],
              });
            } else {
              result.forEach((r) => {
                if (r.store.code === cart?.product?.store?.code) {
                  r.carts = [...r.carts, cart];
                }
              });
            }
          }
        });
        result.forEach((x) => {
          const cartQtys = x.carts.map((x) => x.qty);
          const totalQty = cartQtys.reduce((acc, cur) => acc + cur, 0);
          x.orderedQty = totalQty;
        });
        patchState(store, {
          productsGrouppedByStore: result,
        });
      },
      clearGroupProductByStore() {
        patchState(store, {
          productsGrouppedByStore: [],
        });
      },
      initShippingOption() {
        const grouppedProducts = store.productsGrouppedByStore();
        grouppedProducts.forEach((x) => {
          if (
            x.store.code &&
            x.store.shippingOptions &&
            x.store.shippingOptions.length > 0
          ) {
            const option = x.carts[0].shippingPolicy
              ? x.carts[0].shippingPolicy
              : x.store.shippingOptions[0];
            this.setShippingOptionSelection(x.store.code, option);
          }
        });
        patchState(store, {
          productsGrouppedByStore: grouppedProducts,
        });
      },
      setShippingOptionSelection(
        storeCode: string | undefined,
        option: ShippingOption,
        carts: ShopingCart[] = []
      ) {
        if (storeCode && option) {
          const selection = store
            .selectedShippingOption()
            .set(storeCode, option);
          patchState(store, {
            selectedShippingOption: selection,
          });
          
          this.saveShippingOptionToDB(carts, storeCode, option);
          
        }
      },
      saveShippingOptionToDB(
        carts: ShopingCart[],
        storeCode: string,
        option: ShippingOption
      ) {
        const cartsToDb = <ShoppingCartPayload>{};

        cartsToDb.itemOps = carts.map(c => <ItemOp>{
          merchCode: c.product?.code,
          shippingPolicyCode: `${storeCode}-${option.code}`,
        });

        shoppingService
          .updateShoppingCart(cartsToDb)
          .subscribe((response: AmmShoppingCartBackEnd) => {
            const itemsInDb = response?.items?.map(
              (x) =>
                <ShopingCart>{
                  product: x.merchandise,
                  qty: x?.qty,
                  shippingPolicy: x.shippingPolicy,
                }
            );
            if (itemsInDb) {
              patchState(store, {
                shoppingCarts: itemsInDb,
                orderTotal: response.orderTotal,
                orderSubtotal: response.itemSubTotal,
                shippingFee: response.shippingFee,
              });
            }
          });
      },
      saveAddressToDB(
        address: Address | null | undefined,
        saveAsDefault: boolean = false,
        isShipping: boolean = true,
        reloadData: boolean = true
      ) {
        const cartsToDb = isShipping
          ? <ShoppingCartPayload>{ shippingAddress: address }
          : <ShoppingCartPayload>{ billingAddress: address };

        shoppingService
          .updateShoppingCart(cartsToDb)
          .subscribe((response: AmmShoppingCartBackEnd) => {
            const itemsInDb = response?.items?.map(
              (x) =>
                <ShopingCart>{
                  product: x.merchandise,
                  qty: x?.qty,
                  shippingPolicy: x.shippingPolicy,
                }
            );
            if (itemsInDb) {
              patchState(store, {
                shoppingCarts: itemsInDb,
                orderTotal: response.orderTotal,
                orderSubtotal: response.itemSubTotal,
                shippingFee: response.shippingFee,
              });
            }
            if (saveAsDefault) {
              const id = response.shippingAddress?.id;
              const buyerId = commonStore.buyerInfo()?.id;
              shoppingService
                .changeBuyerInfo({
                  id: buyerId,
                  defaultDeliveryAddress: {
                    id: id,
                  },
                })
                .subscribe(() => {
                  commonStore.loadAllAddresses();
                  commonStore.loadBuyerInfo();
                });
            } else {
              if (reloadData) {
                commonStore.loadAllAddresses();
                commonStore.allAddresses();
              }
            }
          });
      },
      clearShippingOption() {
        const newOptionMap = new Map<string, ShippingOption>();
        patchState(store, {
          selectedShippingOption: newOptionMap,
        });
      },

      updateUndoShoppingCart() {
        // this will be called whenever the shopptingCarts has changes
        patchState(store, {
          undoShoppingCarts: store.shoppingCarts(),
        });
      },
      removeProductFromShoppingCarts(
        product: Product | Favorite,
        shouldAddToUndo: boolean = true
      ) {
        const removedItem = store
          .shoppingCarts()
          .find((x) => x.product?.code === product.code);
        if (removedItem && shouldAddToUndo) {
          patchState(store, {
            undoShoppingCarts: [...store.undoShoppingCarts(), removedItem],
          });
        }
        patchState(store, {
          shoppingCarts: store
            .shoppingCarts()
            .map((x) =>
              x.product?.code === product.code ? { ...x, qty: 0 } : x
            ),
        });
      },
      undoShoppingCartsOperation(productCode: string) {
        const removedItem = store
          .undoShoppingCarts()
          .find((x) => x.product?.code === productCode);

        if (removedItem) {
          patchState(store, {
            shoppingCarts: [
              removedItem,
              ...store.shoppingCarts().filter((x) => x.qty > 0),
            ],
            undoShoppingCarts: store
              .undoShoppingCarts()
              .filter((x) => x.product?.code !== removedItem.product?.code),
          });
        }
      },
      setShortInventoryMerchCodes(codes: string[]) {
        patchState(store, {
          shortInventoryMerchCodes: codes
        })
      },
      clearShoppingCarts() {
        patchState(store, {
          shoppingCarts: [],
        });
      },
      clearUndoShoppingCarts() {
        patchState(store, {
          undoShoppingCarts: [],
        });
      },
      setFinalShoppingCart(sc: AmmShoppingCartBackEnd) {
        patchState(store, {
          ShopingCartBackend: sc,
        });
      },
      clearShoppingCartBackend() {
        patchState(store, {
          ShopingCartBackend: undefined,
        });
      },
      clearShortInventoryMerchCodes() {
          patchState(store, {
                    shortInventoryMerchCodes: []
                  });
      },
      clearAll() {
        this.clearShoppingCarts();
        this.clearShippingOption();
        this.clearUndoShoppingCarts();
        this.clearOrderFees();
        this.clearFavorites();
        this.clearShortInventoryMerchCodes();
      },
      loadRecommondForYou() {
        const storeCodes = store
          .shoppingCarts()
          .filter((x) => x.qty > 0)
          .map((x) => x.product?.store?.code);
        const currentPageIndex = 0;
        const pageSize = 15;
        if (storeCodes && storeCodes.length > 0) {
          productSrv
            .getStoreProducts(storeCodes[0], currentPageIndex, pageSize)
            .subscribe((response) => {
              if (
                response &&
                response.dataList &&
                response.dataList.length > 0
              ) {
                patchState(store, {
                  recommandForYou: response.dataList,
                });
              }
            });
        }
      },
      setShippingAddressOption(
        shippingAddrOption: StripeAddressElementOptions
      ) {
        patchState(store, {
          shippingAddressesOption: shippingAddrOption,
        });
      },
    })
  )
);
